import { useState } from "react";
import { Card } from "@/components/ui/card";
import { Dialog, DialogContent } from "@/components/ui/dialog";
import { Play, Quote } from "lucide-react";

interface TestimonialVideo {
  video_url: string;
  thumbnail_url?: string;
  author: string;
  company: string;
  quote: string;
}

interface BrokerageTestimonialVideosProps {
  videos: TestimonialVideo[];
}

export function BrokerageTestimonialVideos({ videos }: BrokerageTestimonialVideosProps) {
  const [selectedVideo, setSelectedVideo] = useState<string | null>(null);

  // Extract YouTube video ID from URL
  const getYouTubeId = (url: string | null | undefined) => {
    if (!url || typeof url !== 'string') return null;
    const match = url.match(/(?:youtube\.com\/watch\?v=|youtu\.be\/)([^&]+)/);
    return match ? match[1] : null;
  };

  // Generate YouTube thumbnail URL
  const getYouTubeThumbnail = (url: string | null | undefined) => {
    if (!url) return null;
    const videoId = getYouTubeId(url);
    if (!videoId) return null;
    return `https://img.youtube.com/vi/${videoId}/hqdefault.jpg`;
  };

  // Convert to YouTube embed URL
  const getYouTubeEmbedUrl = (url: string | null | undefined) => {
    if (!url) return '';
    const videoId = getYouTubeId(url);
    return videoId ? `https://www.youtube.com/embed/${videoId}?autoplay=1` : url;
  };

  // Filter out invalid videos
  const validVideos = videos.filter(v => v && v.video_url && typeof v.video_url === 'string');

  if (validVideos.length === 0) {
    return null;
  }

  return (
    <>
      <div className="grid md:grid-cols-3 gap-6">
        {validVideos.map((video, index) => (
          <Card
            key={index}
            className="overflow-hidden cursor-pointer hover:shadow-lg transition-shadow group"
            onClick={() => setSelectedVideo(video.video_url)}
          >
            {/* Thumbnail */}
            <div className="relative aspect-video bg-muted">
              {(video.thumbnail_url || getYouTubeThumbnail(video.video_url)) ? (
                <img
                  src={video.thumbnail_url || getYouTubeThumbnail(video.video_url) || ''}
                  alt={`${video.author} testimonial`}
                  className="w-full h-full object-cover"
                  onError={(e) => {
                    const target = e.target as HTMLImageElement;
                    const videoId = getYouTubeId(video.video_url);
                    if (videoId) {
                      if (target.src.includes('hqdefault')) {
                        target.src = `https://img.youtube.com/vi/${videoId}/sddefault.jpg`;
                      } else if (target.src.includes('sddefault')) {
                        target.src = `https://img.youtube.com/vi/${videoId}/mqdefault.jpg`;
                      }
                    }
                  }}
                />
              ) : (
                <div className="w-full h-full flex items-center justify-center bg-muted">
                  <Play className="h-16 w-16 text-muted-foreground" />
                </div>
              )}
              <div className="absolute inset-0 bg-black/40 group-hover:bg-black/50 transition-colors flex items-center justify-center">
                <div className="w-16 h-16 rounded-full bg-primary text-primary-foreground flex items-center justify-center group-hover:scale-110 transition-transform">
                  <Play className="h-8 w-8 ml-1" />
                </div>
              </div>
            </div>

            {/* Content */}
            <div className="p-4 space-y-3">
              <div className="flex items-start gap-2">
                <Quote className="h-4 w-4 text-primary shrink-0 mt-1" />
                <p className="text-sm text-muted-foreground italic line-clamp-3">
                  "{video.quote}"
                </p>
              </div>
              
              <div>
                <div className="font-bold">{video.author}</div>
                <div className="text-sm text-muted-foreground">{video.company}</div>
              </div>
            </div>
          </Card>
        ))}
      </div>

      {/* Video Modal */}
      <Dialog open={!!selectedVideo} onOpenChange={() => setSelectedVideo(null)}>
        <DialogContent className="max-w-4xl p-0">
          {selectedVideo && (
            <div className="aspect-video">
              <iframe
                src={getYouTubeEmbedUrl(selectedVideo)}
                className="w-full h-full"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
              />
            </div>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
}
